// Full-document render of the toppers ReviewsPage: PageSkeleton (the React
// document shell) wrapping the ``reviews_page.html`` layout -- the hero
// background, the customer-review header (hero-content island + write-review
// button), and the shared reviews-viewer island.
//
// This page exercises PageSkeleton's header/footer/messages SUPPRESSION SLOTS.
// In standalone mode (``show_sources=False``, the iframe-embedded variant) the
// template blanked ``{% block header %}`` to an empty ``<header></header>``
// (dropping the site header AND the flash messages, which lived inside the header
// block) and swapped ``{% block footer %}`` for ``.reviews-standalone-footer``:
//   - header slot -> a literal empty ``<header />`` element (NOT ``false``, which
//     would omit the element entirely; the empty ``<header></header>`` must be emitted)
//   - messages slot -> ``false`` (suppressed; the server half is
//     ``build_page_chrome(include_messages=False)`` so the flash queue isn't drained)
//   - footer slot -> the ``.reviews-standalone-footer`` node
// In sourced mode (``show_sources=True``) all three slots stay ``undefined`` -> the
// default site chrome (header w/ messages + footer).
//
// The iovation scripts are rendered page-scoped here (only
// ``tsicommon/reviews/iovation.html`` was included on this page). They are
// injected imperatively from a
// post-hydration effect (IovationScripts) rather than as declarative React
// <script>s: React 19 would hoist a declarative ``<script async src>`` snare to
// <head>, letting it execute before the body IGLOO config on a warm cache and
// silently degrading fingerprinting. ``reviews.ts`` instantiates Flickity on the
// reviews nav at import time from inside the viewer island's client app -- left
// unchanged (no React handlers added here).
import React from "react";
import { IovationScripts } from "tsi-common-react/src/components/IovationScripts/index";

import { Context, templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { ReviewsPageFooterContent } from "../components/ReviewsPageFooterContent";
import { ReviewsPageHeroContent } from "../components/ReviewsPageHeroContent";
import { Template as ReviewsViewer } from "./ReviewsPageTemplate";
import { Template as WriteReviewButton } from "./WriteReviewButtonTemplate";

// Mutable reactivated props (not Immutable<>): this template composes the in-tree
// SiteHeader / SiteFooter chain via PageSkeleton, which is typed with mutable
// reactivated props. The islands accept Immutable props, to which these are
// assignable.
type IProps = templates.ReviewsPageDocumentTemplate;

// ``{% if %}`` guards on StreamField fields are StreamValue truthiness (an
// empty stream is falsy). hero_content / footer_content are distinct nominal
// StreamValue types, so accept either.
const hasStreamValue = (
    field: IProps["page"]["hero_content"] | IProps["page"]["footer_content"],
): boolean => !!field && field.value.length > 0;

const renderStandaloneFooter = (props: IProps): React.JSX.Element => {
    const { page } = props;
    return (
        <div className="reviews-standalone-footer">
            <div className="reviews-standalone-footer__container l-capped-width">
                {hasStreamValue(page.footer_content) ? (
                    <ReviewsPageFooterContent page={page} />
                ) : null}
            </div>
        </div>
    );
};

export const Template = (props: IProps): React.JSX.Element => {
    const { page } = props;
    const chrome = React.useContext(Context).chrome;
    const showSources = page.show_sources;
    const hasHeroContent = hasStreamValue(page.hero_content);

    const heroClassName = `hero hero--reviews${
        props.has_hero_image ? "" : " hero--reviews-empty"
    }`;
    const headerClassName = `customer-review__header${
        showSources ? "" : " customer-review__header--standalone"
    }${hasHeroContent ? "" : " customer-review__header--empty"}`;

    return (
        <PageSkeleton
            pageChrome={props.page_chrome}
            header={showSources ? undefined : <header />}
            footer={showSources ? undefined : renderStandaloneFooter(props)}
            messages={showSources ? undefined : false}
        >
            <div
                className={heroClassName}
                style={{ backgroundImage: `url('${props.hero_bg_url}')` }}
            />
            <div className="l-capped-width reviews-page-container">
                <div className="reviews-app-container">
                    <div className="customer-review l-capped-width">
                        <div className={headerClassName}>
                            <div className="customer-review__header-copy">
                                {hasHeroContent ? (
                                    <ReviewsPageHeroContent page={page} />
                                ) : null}
                            </div>
                            {showSources ? (
                                <WriteReviewButton
                                    brand=""
                                    product_type_whitelist={
                                        props.write_review_product_type_whitelist
                                    }
                                />
                            ) : null}
                        </div>
                        <ReviewsViewer {...props.reviews} />
                    </div>
                </div>
            </div>
            {/* iovation (page-scoped): injected post-hydration to keep the
                IGLOO -> snare -> loader execution order (see IovationScripts). */}
            <IovationScripts
                iovationUrl={props.iovation_url}
                staticBase={chrome.static_base}
            />
        </PageSkeleton>
    );
};
